import ExpandUp from '@mui/icons-material/ExpandLess';
import { formatTranslation } from 'intl';
import { IssueTranslationKeys } from 'intl/keys';
import { Box, Card, CardActionArea } from '@mui/material';
import cn from 'classnames';
import { ProcessIcon } from 'components/common/ProcessIcon';
import { ProcessIconSize } from 'components/common/ProcessIcon/types';
import { useIssueForm } from 'store/issueForm';
import { useCompanies } from 'store/company';
import { useVisibleFieldsMap } from 'store/visibleFields';
import { ButtonIcon } from 'components/general';
import { MemoIssueStage } from 'components/general/IssueStage';
import { IssueStageSize } from 'components/general/IssueStage/types';
import { MemoIssueCardChip } from 'components/inspection/IssueCardPreview/Chip';
import { displayDate } from 'components/table/renderers/cell/dates';
import { toIssueCardNumberFormat } from 'presentation/issue/issueCardNumberFormat';
import {
  Dispatch,
  MouseEvent,
  MouseEventHandler,
  ReactElement,
  SetStateAction,
  TouchEvent,
  TouchEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useProcessesObject } from 'store/process';
import { useProject } from 'store/project';
import { delayCalculatorWithAhead } from 'shared/domain/issue/calculateDelayField';
import { IssueModel } from 'shared/domain/issue/issueModel';
import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import { getUserLabelOptionalByOrganization } from 'shared/domain/user/mapping/toView';
import { Color } from 'shared/utils/colors';
import { IssueCardDetails } from './details';
import { useLastEditString } from './model';
import { IssueCardSquare } from './square';
import { useStyles } from './styles';
import { ExtendedFieldTranslationKeys } from 'intl/keys';
const stopPropagation: TouchEventHandler = (e) => e.stopPropagation();

export function IssueCardPresentational({
  issueId,
  hashtag,
  processCode,
  processColor,
  title,
  company,
  companyFieldLabel,
  plannedDatesLabel,
  plannedDates,
  completionDateLabel,
  completionDates,
  delayLabel,
  completionAmount,
  delays,
  deleted,
  stageId,
  workflowId,
  issueModel,
  onClick,
  isSelected,
  lastEditString,
  isExpanded,
  setIsExpanded,
}: {
  issueId: string;
  hashtag: string;
  processCode: string;
  processColor: Color;
  title: string;
  company: string;
  companyFieldLabel: string;
  plannedDatesLabel: string | undefined;
  plannedDates: (string | undefined | null)[] | undefined;
  completionDateLabel: string | undefined;
  completionDates: (string | undefined | null)[] | undefined;
  delayLabel: string | undefined;
  completionAmount: string;
  delays: (number | null)[];
  deleted: boolean;
  stageId: string;
  workflowId: string;
  lastEditString: string;
  issueModel: IssueModel;
  isExpanded: boolean;
  setIsExpanded: Dispatch<SetStateAction<boolean>>;
  onClick?: (issue: IssueModel) => void;
  isSelected?: boolean;
}): ReactElement {
  const cs = useStyles();
  const cardElementRef = useRef<HTMLElement>(null);
  const intl = useIntl();
  useEffect(() => {
    if (isSelected) {
      cardElementRef.current?.scrollIntoView({
        block: 'center',
        inline: 'center',
        behavior: 'smooth',
      });
    }
  }, [isSelected]);
  const onCardClick = useCallback(
    (e: TouchEvent | MouseEvent) => {
      //ensures proper behavior of clickAwayListener in Documentation pins and areas
      e.stopPropagation();
      if (typeof onClick === 'function') onClick(issueModel);
    },
    [onClick, issueModel]
  );

  const expand: MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      setIsExpanded((p) => !p);
    },
    [setIsExpanded]
  );

  return (
    <Box
      ref={cardElementRef}
      sx={{
        minWidth: 'min(380px, 95%)',
        width: '100%',
      }}
    >
      <Card
        className={cn(cs.card, isSelected ? 'selected' : '')}
        variant='outlined'
        style={{ pointerEvents: 'all' }}
      >
        <CardActionArea onTouchEnd={stopPropagation} onClick={onCardClick}>
          <div
            className={(deleted && cs.deleted) || ''}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              paddingLeft: '10px',
            }}
          >
            <ProcessIcon
              code={processCode}
              color={processColor}
              size={ProcessIconSize.issueCard}
            />
            <div className={cs.cardHeader}>{title}</div>
            <ButtonIcon
              style={{
                transform: isExpanded
                  ? 'rotate(-360deg)'
                  : 'rotate(540deg)',
                transition: 'transform .1s',
              }}
              //ensures proper behavior of clickAwayListener in Documentation pins and areas
              onTouchEnd={stopPropagation}
              onClick={expand}
              component='span'
            >
              <ExpandUp htmlColor={Color.black} />
            </ButtonIcon>
          </div>
          <IssueCardDetails
            hashtag={hashtag}
            company={company}
            companyFieldLabel={companyFieldLabel}
            plannedDatesLabel={plannedDatesLabel}
            plannedDates={plannedDates}
            completionDateLabel={completionDateLabel}
            completionDates={completionDates}
            delayLabel={delayLabel}
            completionAmount={completionAmount}
            delays={delays}
            deleted={deleted}
            stageId={stageId}
            workflowId={workflowId}
            lastEditString={lastEditString}
            isSelected={isSelected}
            isExpanded={isExpanded}
          />
        </CardActionArea>
      </Card>
    </Box>
  );
}

export type IssueCardVariant = 'default' | 'square';

export function IssueCard({
  issue,
  onClick,
  isSelected,
  variant = 'default',
}: {
  issue: IssueModel;
  onClick?: (issue: IssueModel) => void;
  isSelected?: boolean;
  /** `square`: 182px attachment-sized tile with a hover preview (protocol items). */
  variant?: IssueCardVariant;
}): ReactElement | null {
  const intl = useIntl();
  const { issueForm } = useIssueForm();
  const { items: companies } = useCompanies();
  const { timezone, organizationId } = useProject();
  const processes = useProcessesObject();
  const process = processes[issue.process];

  // Early return if process or primaryData is missing
  if (!process || !issue.primaryData) {
    return null;
  }

  const visibleFields = useVisibleFieldsMap()[issue.process];

  const hashtag = issue._id.slice(-4);
  const mappedForm = useMemo(() => {
    const flattened = {};
    issueForm.forEach((field) => {
      flattened[field.name] = field;
    });
    return flattened;
  }, [issueForm]);

  const companiesField = useMemo(() => {
    const executionCompanyField =
      mappedForm[IssueFieldNames.executedByCompanies];
    if (
      visibleFields[IssueFieldNames.executedByCompanies] &&
      executionCompanyField
    ) {
      return {
        values: issue.extendedData.executedByCompanies || [],
        label: executionCompanyField.label,
      };
    } else if (
      visibleFields[IssueFieldNames.subcontractors] &&
      mappedForm[IssueFieldNames.subcontractors]
    ) {
      return {
        values: issue.primaryData?.subcontractors || [],
        label: mappedForm[IssueFieldNames.subcontractors].label,
      };
    }
  }, [visibleFields, issue, mappedForm]);

  const companyString = useMemo(() => {
    if (!companiesField) return '';
    return companiesField.values
      .map((subcontractorId) => {
        const foundCompany = companies.find(
          (company) => company._id === subcontractorId
        );
        return foundCompany?.shortLabel;
      })
      .filter((x) => x)
      .join(', ');
  }, [companiesField, companies]);

  const completionAmountString = useMemo(() => {
    if (
      !visibleFields[IssueFieldNames.completedAmount] ||
      !visibleFields[IssueFieldNames.targetAmount] ||
      !mappedForm[IssueFieldNames.completedAmount] ||
      !mappedForm[IssueFieldNames.targetAmount] ||
      !issue.extendedData.targetAmount
    ) {
      return '';
    }
    const completedAmount = issue.extendedData.completedAmount || 0;

    return `${Math.trunc(
      (completedAmount * 100) / issue.extendedData.targetAmount
    )}% - ${toIssueCardNumberFormat(
      completedAmount
    )}/${toIssueCardNumberFormat(issue.extendedData.targetAmount)}`;
  }, [issue, mappedForm, visibleFields]);

  const plannedDatesWithLabel = useMemo(() => {
    if (
      visibleFields[IssueFieldNames.targetStartDate] &&
      visibleFields[IssueFieldNames.finalStartDate] &&
      mappedForm[IssueFieldNames.targetStartDate] &&
      mappedForm[IssueFieldNames.finalStartDate]
    ) {
      return {
        label: formatTranslation(intl, IssueTranslationKeys.cardPlannedExecutionTime,),
        values: [
          displayDate(issue.extendedData.targetStartDate, timezone),
          displayDate(issue.extendedData.targetCompletionDate, timezone),
        ],
      };
    }

    if (visibleFields[IssueFieldNames.targetCompletionDate]) {
      return {
        label: formatTranslation(intl, IssueTranslationKeys.cardPlannedCompletionDate,),
        values: [
          displayDate(issue.extendedData.targetCompletionDate, timezone),
        ],
      };
    }
  }, [mappedForm, issue, timezone, intl, visibleFields]);

  const finalDatesWithLabel = useMemo(() => {
    if (
      visibleFields[IssueFieldNames.targetStartDate] &&
      visibleFields[IssueFieldNames.finalStartDate] &&
      mappedForm[IssueFieldNames.targetStartDate] &&
      mappedForm[IssueFieldNames.finalStartDate]
    ) {
      return {
        label: formatTranslation(intl, IssueTranslationKeys.cardFinalExecutionTime,),
        values: [
          displayDate(issue.extendedData.finalStartDate, timezone),
          displayDate(issue.extendedData.finalCompletionDate, timezone),
        ],
      };
    }

    if (visibleFields[IssueFieldNames.finalCompletionDate]) {
      return {
        label: formatTranslation(intl, IssueTranslationKeys.cardFinalCompletionDate,),
        values: [
          displayDate(issue.extendedData.finalCompletionDate, timezone),
        ],
      };
    }
  }, [mappedForm, issue, timezone, intl, visibleFields]);

  const delays = useMemo(() => {
    if (
      visibleFields[IssueFieldNames.targetStartDate] &&
      visibleFields[IssueFieldNames.finalStartDate] &&
      mappedForm[IssueFieldNames.targetStartDate] &&
      mappedForm[IssueFieldNames.finalStartDate]
    ) {
      const {
        targetCompletionDate,
        finalCompletionDate,
        targetStartDate,
        finalStartDate,
      } = issue.extendedData;
      const completionDelay = delayCalculatorWithAhead.execute(
        targetCompletionDate,
        finalCompletionDate,
        timezone
      );
      const startDelay = delayCalculatorWithAhead.execute(
        targetStartDate,
        finalStartDate,
        timezone
      );

      return [startDelay, completionDelay];
    }

    if (
      visibleFields[IssueFieldNames.finalCompletionDate] &&
      visibleFields[IssueFieldNames.targetCompletionDate]
    ) {
      const { targetCompletionDate, finalCompletionDate } =
        issue.extendedData;
      const completionDelay = delayCalculatorWithAhead.execute(
        targetCompletionDate,
        finalCompletionDate,
        timezone
      );

      return [completionDelay];
    }
    return [];
  }, [mappedForm, issue, timezone, visibleFields]);

  const [isExpanded, setIsExpanded] = useState(false);

  const isSquare = variant === 'square';

  const lastEditString = useLastEditString(
    intl,
    issue.modifiedAt,
    getUserLabelOptionalByOrganization(issue.modifiedBy, organizationId),
    // The square tile only shows the last edit in its (always expanded) preview.
    isSquare || isExpanded,
    visibleFields
  );

  const commonProps = {
    issueModel: issue,
    processCode: process.code,
    processColor: process.color,
    title: issue.primaryData?.title || '',
    hashtag,
    company: companyString,
    companyFieldLabel: companiesField?.label,
    completionAmount: completionAmountString,
    plannedDates: plannedDatesWithLabel?.values,
    plannedDatesLabel: plannedDatesWithLabel?.label,
    completionDates: finalDatesWithLabel?.values,
    completionDateLabel: finalDatesWithLabel?.label,
    delayLabel:
      visibleFields[IssueFieldNames.finalCompletionDate] &&
      visibleFields[IssueFieldNames.targetCompletionDate]
        ? formatTranslation(intl, IssueTranslationKeys.cardDelay)
        : undefined,
    delays,
    deleted: issue.deleted,
    stageId: issue.stageId,
    workflowId: issue.workflowId,
    onClick,
    isSelected,
    lastEditString,
  };

  if (isSquare) {
    return <IssueCardSquare {...commonProps} />;
  }

  return (
    <IssueCardPresentational
      issueId={issue._id}
      key={issue._id}
      {...commonProps}
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
    />
  );
}
